import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { useQuestionsContext } from "../context/QuestionsContext";
import { postProcessSelectedText } from "../../../../../../core/api/Api";
import { storageToHtml } from "./rich-editor/richEditorUtils";
import "./ChapterActionToolbar.css";

const DevelopIcon = () => (
  <span className="chapter-action-toolbar__develop-icon">
    <i className="fa-solid fa-align-left" />
    <i className="fa-solid fa-plus chapter-action-toolbar__develop-plus" />
  </span>
);

const ACTIONS = [
  { type: "rewrite",        icon: "fa-solid fa-pen",  labelKey: "selection_rewrite" },
  { type: "correct_grammar", icon: "fa-solid fa-check", labelKey: "selection_correct" },
  { type: "format",         iconEl: <DevelopIcon />,  labelKey: "selection_format"  },
];

const getPlainText = (storageContent) => {
  if (!storageContent) return "";
  const html = storageToHtml(storageContent);
  const div = document.createElement("div");
  div.innerHTML = html;
  return div.textContent || div.innerText || "";
};

const ChapterActionToolbar = ({ content, onContentChange }) => {
  const { t } = useTranslation();
  const { isSelfWrite } = useQuestionsContext();
  const [openType, setOpenType] = useState(null);
  const [loadingType, setLoadingType] = useState(null);
  const [hint, setHint] = useState(false);
  const [proposal, setProposal] = useState(null);
  const popupRef = useRef(null);
  const hintTimerRef = useRef(null);

  // Close popup on outside click
  useEffect(() => {
    if (!openType) return;
    const onOutside = (e) => {
      if (popupRef.current && !popupRef.current.contains(e.target)) {
        setOpenType(null);
      }
    };
    document.addEventListener("mousedown", onOutside);
    document.addEventListener("touchstart", onOutside);
    return () => {
      document.removeEventListener("mousedown", onOutside);
      document.removeEventListener("touchstart", onOutside);
    };
  }, [openType]);

  // Auto-hide hint message
  useEffect(() => {
    if (!hint) return;
    hintTimerRef.current = setTimeout(() => setHint(false), 3000);
    return () => clearTimeout(hintTimerRef.current);
  }, [hint]);

  if (!isSelfWrite) return null;

  const handleIconClick = (type) => {
    setOpenType((prev) => (prev === type ? null : type));
    setHint(false);
  };

  const handleWholeChapter = async () => {
    if (!openType || loadingType) return;
    const type = openType;
    const text = getPlainText(content).trim();
    if (!text) return;
    setOpenType(null);
    setLoadingType(type);
    try {
      const response = await postProcessSelectedText({ text, type });
      const newContent = response?.data?.success?.content;
      if (newContent) {
        setProposal({ content: newContent });
      }
    } catch (_err) {
      // silent — original content preserved
    } finally {
      setLoadingType(null);
    }
  };

  const handleSelectPart = () => {
    setOpenType(null);
    setHint(true);
  };

  const acceptProposal = () => {
    if (!proposal) return;
    onContentChange(proposal.content);
    setProposal(null);
  };

  const rejectProposal = () => setProposal(null);

  return (
    <>
    <div className="chapter-action-toolbar">
      {ACTIONS.map(({ type, icon, iconEl, labelKey }) => (
        <div key={type} className="chapter-action-toolbar__item">
          <button
            type="button"
            className={`chapter-action-toolbar__btn${openType === type ? " is-open" : ""}${loadingType === type ? " is-loading" : ""}`}
            onClick={() => handleIconClick(type)}
            disabled={!!loadingType}
            title={t(labelKey)}
          >
            {loadingType === type ? (
              <i className="fa-solid fa-spinner fa-spin" />
            ) : iconEl ? iconEl : (
              <i className={icon} />
            )}
          </button>

          {openType === type && (
            <div className="chapter-action-toolbar__popup" ref={popupRef}>
              <p className="chapter-action-toolbar__popup-title">{t(labelKey)}</p>
              <button
                type="button"
                className="chapter-action-toolbar__popup-btn chapter-action-toolbar__popup-btn--primary"
                onClick={handleWholeChapter}
              >
                <i className="fa-solid fa-file-lines me-2" />
                {t("chapter_action_whole_chapter")}
              </button>
              <button
                type="button"
                className="chapter-action-toolbar__popup-btn chapter-action-toolbar__popup-btn--secondary"
                onClick={handleSelectPart}
              >
                <i className="fa-solid fa-i-cursor me-2" />
                {t("chapter_action_select_part")}
              </button>
            </div>
          )}
        </div>
      ))}

      {hint && (
        <div className="chapter-action-toolbar__hint">
          <i className="fa-solid fa-arrow-right me-1" />
          {t("chapter_action_hint")}
        </div>
      )}
    </div>

    {proposal && createPortal(
      <div className="chapter-proposal-overlay" onMouseDown={(e) => e.stopPropagation()}>
        <div className="chapter-proposal-card">
          <div className="chapter-proposal-card__header">
            <i className="fa-solid fa-wand-magic-sparkles me-2" />
            {t("selection_suggestion")}
          </div>
          <div className="chapter-proposal-card__body">
            {proposal.content}
          </div>
          <div className="chapter-proposal-card__footer">
            <button
              type="button"
              className="chapter-proposal-card__btn chapter-proposal-card__btn--reject"
              onClick={rejectProposal}
            >
              {t("selection_reject")}
            </button>
            <button
              type="button"
              className="chapter-proposal-card__btn chapter-proposal-card__btn--accept"
              onClick={acceptProposal}
            >
              {t("selection_accept")}
            </button>
          </div>
        </div>
      </div>,
      document.body
    )}
  </>
  );
};

export default ChapterActionToolbar;
